import * as Sentry from '@sentry/react';
import {
    IS_LIVE_PROD,
    IS_EDITOR_MODE,
    SENTRY_REACT_DSN,
    SENTRY_TRACES_SAMPLE_RATE,
    IS_DEV
} from 'config/envVariable';
import {
    isOneOfErrors,
    isChunkLoadError,
    isFetchNetworkError
} from 'client/utils/errorObjectChecker';

export function initErrorLogService() {
    if (IS_LIVE_PROD || IS_DEV) {
        const DEFAULT_TRACES_SAMPLE_RATE = 0.00001;
        const tracesSampleRate = isNaN(parseFloat(SENTRY_TRACES_SAMPLE_RATE))
            ? DEFAULT_TRACES_SAMPLE_RATE
            : parseFloat(SENTRY_TRACES_SAMPLE_RATE) || DEFAULT_TRACES_SAMPLE_RATE;

        Sentry.init({
            dsn: SENTRY_REACT_DSN,
            environment: IS_LIVE_PROD ? 'production' : 'dev',
            normalizeDepth: 8,
            maxBreadcrumbs: 80,
            integrations(integrations) {
                return [
                    ...integrations.filter(i => i.name !== 'GlobalHandlers'),
                    Sentry.browserTracingIntegration()
                ];
            },
            beforeSend(event, hint) {
                const { originalException = {}, captureContext: { extra } = {} } = hint;

                // 在編輯器模式下，我們特別想要捕獲主動抛出的錯誤
                if (IS_EDITOR_MODE && originalException.isActivelyCatchErr) {
                    return event;
                }

                // 明確標記 forceReport 的事件（例：ErrorBoundary 的 chunk load 失敗診斷）
                // 豁免下面的網路/資源錯誤過濾，否則永遠查不到「載入失敗」頁的成因。
                if (originalException.isForcedSentryReport) return event;

                // 對於非編輯器模式，使用原本的過濾邏輯
                if (
                    isNetworkResourceError(originalException) ||
                    isNetworkResourceError(extra) ||
                    !originalException.isActivelyCatchErr
                ) {
                    return null;
                }
                return event;
            },
            tracesSampleRate
        });
    }
}

/**
 * 將非 Error 物件包裝成 Error 實例
 * @param {Object} obj - 原始物件
 * @returns {Error} Error 實例
 */
function wrapAsError(obj) {
    const errType = obj?.extraInfo?.errType || obj?.errType || 'UNKNOWN_ERROR';
    const error = new Error(errType);
    error.extraInfo = obj?.extraInfo || obj || {};
    return error;
}

// Sentry 對單一 tag 值的長度上限；超過會被截斷或整筆丟棄，這裡先自行截斷保住可用性
const MAX_TAG_VALUE_LENGTH = 200;

/**
 * 把 tags 正規化成 Sentry 接受的形狀：值一律轉字串、略過空值、過長截斷。
 *
 * 之所以要轉字串：Sentry 的 tag 值本來就只有字串型別，直接丟 boolean / number 進去
 * 在部分 SDK 版本會被整組忽略。`false` 與 `0` 是有意義的值，必須保留（只排除
 * null / undefined / 空字串）。
 *
 * @param {Object<string, *>} [tags]
 * @returns {Object<string, string>|null} 沒有任何有效 tag 時回傳 null
 */
function normalizeTags(tags) {
    if (!tags || typeof tags !== 'object') return null;

    const normalized = {};
    for (const [key, value] of Object.entries(tags)) {
        if (value === null || value === undefined || value === '') continue;
        const stringValue = String(value);
        if (!stringValue) continue;
        normalized[key] =
            stringValue.length > MAX_TAG_VALUE_LENGTH
                ? stringValue.slice(0, MAX_TAG_VALUE_LENGTH)
                : stringValue;
    }

    return Object.keys(normalized).length > 0 ? normalized : null;
}

/**
 * Sentry 事件級別。**由我們送事件時自己填**，不填就是 `ERROR`。
 * 嚴重度由高到低：`FATAL` > `ERROR` > `WARNING` > `INFO` > `DEBUG`。
 *
 * ⚠️ level 只影響「看起來多嚴重」與「能不能被 filter 掉」。它**不會**減少事件量或
 * 配額，也**不會**讓同一個 issue 分成兩組（Sentry 分組看的是 stack trace，不看 level）。
 * 目前專案的 alert 規則也沒有設 level 條件，所以降級**不等於**不會被通知。
 */
export const SENTRY_LEVEL = {
    FATAL: 'fatal',
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info',
    DEBUG: 'debug'
};

/**
 * @param {Error|Object} err - Error 實例或可被包成 Error 的物件
 * @param {Object} [options]
 * @param {string} [options.level] - 事件級別，值請用 `SENTRY_LEVEL` 的成員，不要寫字面字串。
 *   不帶時沿用 Sentry 預設（`ERROR`）。給「已知的相容性偵測」這類非故障訊號用，
 *   讓它們不要混在真正的 error 裡（例：AI Web Game 的 CSP 違規遙測）。
 * @param {string[]} [options.fingerprint] - 自訂分組指紋。Sentry 預設用 stack trace + message 分組，
 *   當 message 內含 UUID / 流水號時，同一種問題會被拆成數百個 issue。帶入已正規化的
 *   指紋（例：`['init-api-timeout', '/promotions/{uuid}/infos']`）即可把它們併回同一組。
 *   ⚠️ 這只減少 issue 數量，不會減少事件數與配額。
 * @param {Object<string, *>} [options.tags] - 可搜尋 / 可統計的索引欄位。
 *   `extra`（即 `err.extraInfo`）只有點進單一事件才看得到，**無法搜尋也無法 group by**；
 *   放進 tags 的欄位才會被 Sentry 建索引，能在 issue 頁面直接看到各值的分布
 *   （例：逾時事件裡 `inflight_stage` 有幾成是 `pre-request-dispatch`），也才能寫進
 *   查詢條件、Dashboard 與 alert 規則。
 *   ⚠️ tag 會建索引，**只放低基數欄位**。像 requestId 這種每筆都不同的值會撐爆索引並被
 *   Sentry 丟棄，該留在 `extra`。值一律轉成字串（`false` → `'false'`），空值會被略過。
 *   慣例用 snake_case 命名，與 Sentry 內建 tag 一致。
 * @param {boolean} [options.forceReport] - 強制送出，略過 `beforeSend` 對 chunk load /
 *   網路類錯誤的預設過濾。這類錯誤平常量大且多半無法行動才被濾掉；只有在「這一筆
 *   就是我們要查的目標」時才開（例：ErrorBoundary 顯示「載入失敗」頁時的診斷事件）。
 */
export function sentryCaptureException(err, { level, fingerprint, tags, forceReport } = {}) {
    try {
        // 自動確保是 Error 實例
        const error = err instanceof Error ? err : wrapAsError(err);

        const hasFingerprint = Array.isArray(fingerprint) && fingerprint.length > 0;
        const normalizedTags = normalizeTags(tags);
        const options = {
            extra: { ...(error.extraInfo ?? {}), deviceDate: new Date().toString() },
            ...(level ? { level } : {}),
            ...(hasFingerprint ? { fingerprint: fingerprint.map(String) } : {}),
            ...(normalizedTags ? { tags: normalizedTags } : {})
        };
        error.isActivelyCatchErr = true;
        if (forceReport) error.isForcedSentryReport = true;

        return Sentry.captureException(error, options);
    } catch (error) {
        console.error('Error on sentry capture:', error);
    }
}
function isNetworkResourceError(errObj = {}) {
    return isOneOfErrors([isChunkLoadError, isFetchNetworkError], errObj);
}
